<template>
  <div class="preview-section">
    <div v-if="selectedElement" class="card card-preview">
      <cardHeader  :numOrder="getQuestionNumber() === 'N/A' ? numOrder :  getQuestionNumber() " :elementType="selectedElement.el_Type" mode="Preview" :isAgent="isAgent" />
      <div class="card-body">

        <div v-if="showReport && isCorrect && selectedElement.feedback.correct"
  class="PositiveFeebackContainer">
  {{ selectedElement.feedback.correct }}
</div>
<div v-else-if="showReport && !isCorrect && selectedElement.feedback.incorrect" class="NegativeFeebackContainer">
  {{ selectedElement.feedback.incorrect }}
</div>

        <h3>{{ selectedElement.el_Text }}</h3>

        <div class="quill-editor" ref="quillEditor" v-show="hasQuillContent"></div>

        <div class="block-options mt-3">
          <div v-for="(option, index) in displayedOptions" :key="option.id"
  :class="['block-option', isSelected(option.id) ? 'selected' : '', props.readOnly ? 'readonly-mode' : '']"
  :style="computedBlockStyle"
  @click="!props.readOnly && toggleOption(option.id)">
            <div class="option-content">
              <span v-if="getIndexType !== 'none'" class="option-index">{{ getOptionIndex(index) }}</span>
              <div class="option-text" v-html="option.richText"></div>
              <span v-if="isSelected(option.id)" class="check-mark">✅</span>
            </div>
          </div>
        </div>

        <!-- Hint -->
        <div v-if="displayHintButton" class="hintContainer mt-3">
          <button class="btn btn-sm btn-primary" @click="showHint = true">{{$t('Hint')}}!</button>
          <div v-if="showHint" class="hintMessage mt-2">{{ selectedElement.tip }}</div>
        </div>
      </div>

      <!-- Report -->
      <div class="ReportContainer" v-if="showReport">
        <h5>Results:</h5>
        <p><strong>Your Answers:</strong> {{ results.selectedLabels.join(', ') }}</p>
        <p><strong>Score:</strong> {{ results.score }}</p>
        <p><strong>Max Score:</strong> {{ computedMaxScore }}</p>
        <p><strong>Penalties:</strong> {{ results.error }}</p>
        <p><strong>Hint used:</strong> {{ results.hint }}</p>


      </div>

      <cardFooterLearningMode 
        v-if="(publicationStore.publicationData?.mode) !== 'exam'"
        :showReport="showReport" 
        :submitAnswers="showReportOnSubmit" 
        :closeReport="closeReport"
        :resetSelections="resetSelections" />

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watchEffect, nextTick, watch } from 'vue';
import Quill from 'quill';
import cardHeader from './cardHeader.vue';
import cardFooterLearningMode from './cardFooterLearningMode.vue';
import { useResponsesStore } from '../../stores/useResponsesStore.js';
import { usePublicationStore } from '@/stores/usePublicationStore';

const publicationStore = usePublicationStore();
const responsesStore = useResponsesStore();

const token = localStorage.getItem('token');
const decodedToken = decodeToken(token);
const userId = decodedToken?._id || decodedToken?.sub;
const isAgent = ref(decodedToken?.agent === true);
const agentLevel = ref(decodedToken?.agentLevel || null);


const props = defineProps({
  question: { type: Object, required: true },
  testData: { type: Object, required: true },
  numOrder: [Number, null],
  questionIndexMap: { type: Object, required: false },
  restoredAnswer: { type: [String, Array, null], default: null },
  readOnly: { type: Boolean, default: false }
});

const selectedElement = computed(() => props.question);
const isMultiple = computed(() => selectedElement.value?.multiple === true);
const displayHintButton = computed(() => !!selectedElement.value?.tip);

const quillEditor = ref(null);
let quillInstance = null;
const hasQuillContent = ref(false);

const showHint = ref(false);
const showReport = ref(false);

const results = ref({
  score: 0,
  error: 0,
  mark: 0,
  selectedOptions: [],
  selectedLabels: [],
  hint: false
});

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const displayedOptions = ref([]);
const studentAnswers = ref(isMultiple.value ? [] : null);

const computedMaxScore = computed(() => {
  if (!selectedElement.value) return 1;
  const opts = selectedElement.value.options || [];
  if (isMultiple.value) {
    return opts
      .filter(opt => opt.isCorrect && Number(opt.weight) > 0)
      .reduce((sum, opt) => sum + Number(opt.weight), 0);
  } else {
    const correct = opts.find(o => o.isCorrect);
    return correct ? Number(correct.weight || 1) : 1;
  }
});

const isCorrect = computed(() => {
  const max = computedMaxScore.value ?? 1;
  return (results.value.mark * 100) >= 100;
});

const showReportOnSubmit = () => {
  showReport.value = true;
}

const closeReport = () => {
  showReport.value = false;
}

// Ajuster dynamiquement la hauteur du conteneur Quill
const adjustQuillHeight = () => {
  if (quillInstance) {
    const contentHeight = quillInstance.root.scrollHeight;
    quillEditor.value.style.overflow = 'hidden';
  }
};

onMounted(async () => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      readOnly: true,
      modules: { toolbar: false }
    });
    
    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText;
      hasQuillContent.value = selectedElement.value.el_RichText?.trim() !== '' && 
                             selectedElement.value.el_RichText !== '<p><br></p>';
    }
    
    adjustQuillHeight();
  }

  displayedOptions.value = selectedElement.value?.randomized
    ? shuffleArray(selectedElement.value.options)
    : [...selectedElement.value.options];

  // 🔁 restauration depuis props (navigation arrière)
  if (props.restoredAnswer !== null && props.restoredAnswer !== undefined) {
    studentAnswers.value = isMultiple.value
      ? [...props.restoredAnswer]
      : props.restoredAnswer[0];
  }

  // 🔁 restauration depuis Pinia
  const saved = responsesStore.getResponseForQuestion(userId, selectedElement.value.el_ID);
  if (saved) {
    studentAnswers.value = isMultiple.value
      ? [...(saved.selectedOptions || [])]
      : saved.selectedOptions?.[0] || null;
  }

  await nextTick();
  displayedOptions.value = [...displayedOptions.value]; // force rerender
  
  // Auto-réponse pour les agents après un délai pour laisser l'interface se charger
  if (isAgent.value && agentLevel.value) {
    setTimeout(() => {
      autoAnswerForAgent();
    }, 1500); // Délai pour laisser l'interface se charger
  }
});

// Observer uniquement el_RichText pour les mises à jour en temps réel
watch(
  () => selectedElement.value?.el_RichText,
  (newValue) => {
    if (quillInstance && newValue !== quillInstance.root.innerHTML) {
      quillInstance.root.innerHTML = newValue || '';
      hasQuillContent.value = newValue && newValue.trim() !== '' && newValue !== '<p><br></p>';
      adjustQuillHeight();
    }
  }
);

watchEffect(() => {
  if (!selectedElement.value) return;
  results.value = {
    score: 0, error: 0, mark: 0, selectedOptions: [], selectedLabels: [], hint: false
  };
  showReport.value = false;

  displayedOptions.value = selectedElement.value.randomized
    ? shuffleArray(selectedElement.value.options)
    : [...selectedElement.value.options];
});

const toggleOption = (id) => {
  if (!isMultiple.value) {
    studentAnswers.value = id;
  } else {
    const index = studentAnswers.value.indexOf(id);
    if (index === -1) studentAnswers.value.push(id);
    else studentAnswers.value.splice(index, 1);
  }

    // Appel à la nouvelle fonction pour gérer la soumission intermédiaire
    handleInputChange(id);

  // Sauvegarde immédiate si mode exam
  if (publicationStore.publicationData?.mode === 'exam') {
    autoSaveAnswer();
  }
};


const isSelected = (id) => {
  return isMultiple.value ? studentAnswers.value.includes(id) : studentAnswers.value === id;
};

function autoSaveAnswer() {
  const selectedIds = isMultiple.value ? studentAnswers.value : [studentAnswers.value];
  const selectedLabels = selectedElement.value.options
    .filter(opt => selectedIds.includes(opt.id))
    .map(opt => stripHtml(opt.richText));

  const maxScore = computedMaxScore.value;

  // ✅ Vérifier si l'utilisateur a vraiment répondu
  const hasAnswered = isMultiple.value 
    ? (Array.isArray(studentAnswers.value) && studentAnswers.value.length > 0)
    : (studentAnswers.value !== null && studentAnswers.value !== undefined);

  const response = {
    userId,
    questionId: selectedElement.value.el_ID,
    type: selectedElement.value.el_Type,
    questionLabel: selectedElement.value.el_Text,
    selectedOptions: selectedIds,
    selectedLabels,
    timestamp: new Date().toISOString(),
    answered: hasAnswered, // ✅ CORRIGÉ: Utilise la vraie logique de réponse  
    maxScore: maxScore,
    score: 0, // on ne corrige pas en mode exam
    hint: showHint.value
  };

  responsesStore.saveResponse(response);

  if (props.testData.strategy === 'realTime') {
    responsesStore.sendResponsesToDatabase('realTime', userId);
  }

  console.log("💾 Auto-save réponse (exam mode):", response);
}



const submitAnswers = () => {
  if (!selectedElement.value || !studentAnswers.value || (Array.isArray(studentAnswers.value) && studentAnswers.value.length === 0)) {
    alert('Please choose an answer!');
    return;
  }

  const selectedIds = isMultiple.value ? studentAnswers.value : [studentAnswers.value];
  results.value.selectedOptions = [];
  results.value.selectedLabels = [];
  results.value.score = 0;
  results.value.error = 0;

  selectedElement.value.options.forEach(opt => {
    if (selectedIds.includes(opt.id)) {
      results.value.selectedOptions.push(opt.id);
      results.value.selectedLabels.push(stripHtml(opt.richText));
      
      const weight = parseFloat(opt.weight) || 0;
      
      if (opt.isCorrect) {
        results.value.score += weight;
      } else if (weight < 0) {
        results.value.error += Math.abs(weight);
        results.value.score += weight;
      }
    }
  });

  const maxScore = computedMaxScore.value;
  results.value.score = Math.max(0, results.value.score); // Évite score négatif
  results.value.mark = Math.min(1, results.value.score / maxScore); // Valeur entre 0 et 1
  results.value.hint = showHint.value;
  

  const response = {
    userId,
    questionId: selectedElement.value.el_ID,
    type: selectedElement.value.el_Type,
    questionLabel: selectedElement.value.el_Text,
    selectedOptions: results.value.selectedOptions,
    selectedLabels: results.value.selectedLabels,
    timestamp: new Date().toISOString(),
    answered: studentAnswers.value, // ✅ CORRIGÉ: Utilise la vraie réponse de l'étudiant
    maxScore: maxScore,
    score: results.value.score,
    hint: results.value.hint
  };

  responsesStore.saveResponse(response);

  if (props.testData.strategy === 'realTime') {
    responsesStore.sendResponsesToDatabase('realTime', userId);
  }
};

const stripHtml = (html) => html.replace(/<[^>]*>?/gm, '').trim();

// Fonction pour auto-réponse basée sur l'agentLevel pour l'interaction ChoiceBlock
const autoAnswerForAgent = () => {
  if (!isAgent.value || !agentLevel.value || !selectedElement.value?.options) return;
  
  const options = selectedElement.value.options;
  const correctOptions = options.filter(option => option.isCorrect);
  const incorrectOptions = options.filter(option => !option.isCorrect);
  
  if (agentLevel.value === 'A') {
    // Niveau A: choisir toutes les bonnes réponses (85-100% succès)
    if (isMultiple.value) {
      studentAnswers.value = correctOptions.map(option => option.id);
    } else {
      // Pour choix simple, prendre la première bonne réponse
      studentAnswers.value = correctOptions.length > 0 ? correctOptions[0].id : null;
    }
  } else if (agentLevel.value === 'B') {
    // Niveau B: taux de succès ciblé 40-84% (moyenne ~62%)
    // 38% de chances de donner une mauvaise réponse
    const shouldGiveWrongAnswer = Math.random() < 0.38;
    
    if (shouldGiveWrongAnswer && incorrectOptions.length > 0) {
      // Donner une mauvaise réponse
      if (isMultiple.value) {
        const randomIncorrect = incorrectOptions[Math.floor(Math.random() * incorrectOptions.length)];
        studentAnswers.value = [randomIncorrect.id];
      } else {
        const randomIncorrect = incorrectOptions[Math.floor(Math.random() * incorrectOptions.length)];
        studentAnswers.value = randomIncorrect.id;
      }
    } else {
      // Donner la bonne réponse
      if (isMultiple.value) {
        studentAnswers.value = correctOptions.map(option => option.id);
      } else {
        studentAnswers.value = correctOptions.length > 0 ? correctOptions[0].id : null;
      }
    }
  } else if (agentLevel.value === 'C') {
    // Niveau C: taux de succès ciblé <40% (moyenne ~20%)
    // 80% de chances de donner une mauvaise réponse
    const shouldGiveWrongAnswer = Math.random() < 0.8;
    
    if (shouldGiveWrongAnswer && incorrectOptions.length > 0) {
      // Donner une mauvaise réponse
      if (isMultiple.value) {
        const randomIncorrect = incorrectOptions[Math.floor(Math.random() * incorrectOptions.length)];
        studentAnswers.value = [randomIncorrect.id];
      } else {
        const randomIncorrect = incorrectOptions[Math.floor(Math.random() * incorrectOptions.length)];
        studentAnswers.value = randomIncorrect.id;
      }
    } else {
      // Donner la bonne réponse
      if (isMultiple.value) {
        studentAnswers.value = correctOptions.map(option => option.id);
      } else {
        studentAnswers.value = correctOptions.length > 0 ? correctOptions[0].id : null;
      }
    }
  }
  
  // Déclencher automatiquement la sauvegarde après un court délai
  setTimeout(() => {
    // Simuler le clic sur l'option pour déclencher les mécanismes existants
    if (studentAnswers.value !== null && 
        (Array.isArray(studentAnswers.value) ? studentAnswers.value.length > 0 : true)) {
      
      const optionToClick = Array.isArray(studentAnswers.value) ? studentAnswers.value[0] : studentAnswers.value;
      handleInputChange(optionToClick);
      
      // Ne pas afficher automatiquement le report - réservé au learning mode
    }
  }, 1000);
};

const resetSelections = () => {
  studentAnswers.value = isMultiple.value ? [] : null;
  showHint.value = false;
  showReport.value = false;
  results.value = { score: 0, error: 0, mark: 0, selectedOptions: [], selectedLabels: [], hint: false };
  displayedOptions.value = selectedElement.value.randomized
    ? shuffleArray([...selectedElement.value.options])
    : [...selectedElement.value.options];
};


const handleInputChange = (optionId) => {
  console.log("Option sélectionnée :", optionId);

  // Appel direct à submitAnswers
  submitAnswers();
};

function decodeToken(token) {
  if (!token) {
    console.error('Le token est null ou non défini');
    return null;
  }
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    console.error('Erreur lors du décodage du token :', error);
    return null;
  }
}

const getQuestionNumber = () => {
  if (!props.questionIndexMap || typeof props.questionIndexMap !== 'object') {
    console.error("❌ questionIndexMap est indéfini ou n'est pas un objet.");
    return "N/A";
  }
  return props.questionIndexMap[props.question.el_ID] ?? "N/A";
};

// Index type computed
const getIndexType = computed(() => {
  return selectedElement.value?.blockStyle?.indexType || 'letters';
});

// Generate option index (A, B, C or 1, 2, 3)
const getOptionIndex = (index) => {
  const indexType = getIndexType.value;

  if (indexType === 'letters') {
    return String.fromCharCode(65 + index) + ' - '; // A, B, C...
  } else if (indexType === 'numbers') {
    return (index + 1) + ' - '; // 1, 2, 3...
  }
  return '';
};

// Computed style for block options
const computedBlockStyle = computed(() => {
  const style = selectedElement.value?.blockStyle;
  if (!style) return {};

  const textAlign = style.textAlign || 'left';

  const cssStyle = {
    borderColor: style.borderColor || '#cccccc',
    borderWidth: `${style.borderWidth || 1}px`,
    borderRadius: `${style.borderRadius || 8}px`,
    width: `${style.width || 100}%`,
    minHeight: `${style.minHeight || 100}px`,
    textAlign: textAlign,
    fontSize: `${style.fontSize || 16}px`,
    color: style.fontColor || '#000000',
    padding: `${style.padding || 16}px`,
    margin: `${style.margin || 8}px`,
    display: 'flex',
    justifyContent: textAlign === 'center' ? 'center' : (textAlign === 'right' ? 'flex-end' : 'flex-start')
  };

  // Gradient priority over solid background
  if (style.gradientColor && style.gradientColor !== '') {
    const direction = style.gradientDirection || 'to right';
    cssStyle.background = `linear-gradient(${direction}, ${style.backgroundColor || '#ffffff'}, ${style.gradientColor})`;
  } else {
    cssStyle.backgroundColor = style.backgroundColor || '#ffffff';
  }

  if (style.backgroundImage) {
    cssStyle.backgroundImage = `url(${style.backgroundImage})`;
    cssStyle.backgroundSize = style.backgroundSize || 'cover';
    cssStyle.backgroundRepeat = style.backgroundRepeat || 'no-repeat';
    cssStyle.backgroundPosition = 'center';
  }

  return cssStyle;
});
</script>


<style scoped>
.block-option {
  border: 1px solid #ccc;
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
}

.block-option.selected {
  background-color: #cce5ff;
  border-color: #58595b;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.block-option:hover {
  border-width: 2px;
  border-color: #8999a9;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}

.quill-editor {
  min-height: max-content!important;
  height: auto!important;
  border: 1px solid #ccc;
  padding: 10px;
  margin-bottom: 1rem;
  overflow: hidden !important;
  transition: height 0.3s ease;
}


  /* Cacher l'élément clipboard de Quill */
  .quill-editor :deep(.ql-clipboard) {
  display: none !important;
}

.hintMessage {
  border: green 1px solid;
  border-radius: 5px;
  padding: 10px;
  background-color: rgb(222, 235, 222);
}

.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
}

.PositiveFeebackContainer {
  background-color: rgb(222, 246, 224);
  padding: 10px;
  border: 1px green dotted;
  border-radius: 5px;
}

.NegativeFeebackContainer {
  background-color: rgb(246, 234, 235);
  padding: 10px;
  border: 1px red dotted;
  border-radius: 5px;
}

.btn-console {
  background-color: #4e3636;
  color: white;
}

.readonly-mode {
  opacity: 0.6;
  pointer-events: none; /* Bloque tout (survol, clic, etc.) */
  cursor: not-allowed;
}

.block-option .option-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: inherit;
}

.option-index {
  font-weight: 600;
  color: inherit;
  flex-shrink: 0;
  min-width: 30px;
  font-size: inherit;
  line-height: inherit;
}

.option-text {
  flex: 1;
}

.option-text :deep(p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
}

.option-text :deep(*) {
  font-size: inherit !important;
}

.check-mark {
  margin-left: 8px;
  flex-shrink: 0;
  animation: checkAppear 0.3s ease-in-out;
}

@keyframes checkAppear {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

</style>
